/**
 * Sidebar HUD: title block, score + high score, stage/flags/lives, BOOST
 * timer, fuel bar. Sits to the right of the play area; never overlaps the
 * maze. Reads state, writes pixels — never mutates.
 *
 * Banner overlays (CHALLENGING STAGE, PAUSED, STAGE CLEAR, etc.) are centered
 * over the play area (not the whole canvas), so the sidebar stays readable
 * during those phases.
 */

import type { GameState } from '../sim/types.ts';
import { LAYOUT } from './layout.ts';

const COLOR = {
  hudBg: 'rgba(6, 6, 12, 0.92)',
  hudBorder: '#1a2742',
  divider: '#1a2742',
  text: '#e6e6f0',
  textDim: '#7aa2f7',
  textFaint: '#475174',
  title: '#cdd6f4',
  titleAccent: '#7aa2f7',
  fuelHigh: '#a6e3a1',
  fuelMid: '#f7c873',
  fuelLow: '#f38ba8',
  fuelTrack: '#1a2742',
  banner: '#f78fb3',
  boost: '#fab387',
} as const;

export type HudInputs = {
  /** Persisted high score; HUD rolls live to max(state.score, highScore). */
  readonly highScore: number;
  readonly logo?: CanvasImageSource | undefined;
};

export function drawHud(ctx: CanvasRenderingContext2D, state: GameState, ri: HudInputs): void {
  const sx = LAYOUT.sidebarX;
  const sw = LAYOUT.sidebarW;
  const sh = LAYOUT.sidebarH;

  // Sidebar panel and left divider.
  ctx.fillStyle = COLOR.hudBg;
  ctx.fillRect(sx, 0, sw, sh);
  ctx.fillStyle = COLOR.divider;
  ctx.fillRect(sx, 0, 1, sh);

  const colX = sx + 12;
  const valX = sx + sw - 12;
  let y = 16;

  // Title block.
  if (ri.logo) {
    drawLogo(ctx, ri.logo, sx + 14, 8, sw - 28, 44);
    y = 62;
  } else {
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillStyle = COLOR.title;
    ctx.font = 'bold 18px ui-monospace, Menlo, monospace';
    ctx.fillText('RALLY', sx + sw / 2, y + 4);
    y += 18;
    ctx.fillStyle = COLOR.titleAccent;
    ctx.fillText('-XY', sx + sw / 2, y + 4);
    y += 22;
  }

  ctx.fillStyle = COLOR.hudBorder;
  ctx.fillRect(sx + 12, y, sw - 24, 1);
  y += 12;

  // Score block.
  ctx.textAlign = 'left';
  ctx.font = '10px ui-monospace, Menlo, monospace';
  ctx.fillStyle = COLOR.textFaint;
  ctx.fillText('SCORE', colX, y);
  ctx.textAlign = 'right';
  ctx.fillStyle = COLOR.text;
  ctx.font = 'bold 14px ui-monospace, Menlo, monospace';
  ctx.fillText(state.score.toString().padStart(7, '0'), valX, y);
  y += 20;

  ctx.textAlign = 'left';
  ctx.font = '10px ui-monospace, Menlo, monospace';
  ctx.fillStyle = COLOR.textFaint;
  ctx.fillText('HIGH', colX, y);
  ctx.textAlign = 'right';
  const liveHigh = Math.max(ri.highScore, state.score);
  ctx.fillStyle = COLOR.textDim;
  ctx.font = 'bold 12px ui-monospace, Menlo, monospace';
  ctx.fillText(liveHigh.toString().padStart(7, '0'), valX, y);
  y += 22;

  ctx.fillStyle = COLOR.hudBorder;
  ctx.fillRect(sx + 12, y, sw - 24, 1);
  y += 10;

  // Stage / flags / lives.
  drawRow(ctx, colX, valX, y, 'STAGE', `${state.stage}${state.challenging ? '*' : ''}`);
  y += 16;
  const yellowRemaining = 10 - state.yellowCollected;
  drawRow(ctx, colX, valX, y, 'FLAGS', `${yellowRemaining}/10`);
  y += 16;
  drawRow(ctx, colX, valX, y, 'LIVES', String(state.lives));
  y += 22;

  // Speed-boost timer.
  if (state.tick < state.speedBoostUntil) {
    const remainingTicks = state.speedBoostUntil - state.tick;
    const remainingSec = Math.ceil(remainingTicks / 60);
    ctx.textAlign = 'left';
    ctx.font = '10px ui-monospace, Menlo, monospace';
    ctx.fillStyle = COLOR.textFaint;
    ctx.fillText('BOOST', colX, y);
    ctx.textAlign = 'right';
    ctx.fillStyle = COLOR.boost;
    ctx.font = 'bold 12px ui-monospace, Menlo, monospace';
    ctx.fillText(`${remainingSec}s`, valX, y);
  }
  y += 22;

  // Fuel bar.
  ctx.textAlign = 'left';
  ctx.font = '10px ui-monospace, Menlo, monospace';
  ctx.fillStyle = COLOR.textFaint;
  ctx.fillText('FUEL', colX, y);
  y += 10;
  const fuelBarW = sw - 24;
  const fuelBarH = 10;
  const fuelBarX = colX;
  const fuelBarY = y;
  const ratio = state.config.fuelMax > 0 ? state.fuel / state.config.fuelMax : 0;
  ctx.fillStyle = COLOR.fuelTrack;
  ctx.fillRect(fuelBarX, fuelBarY, fuelBarW, fuelBarH);
  const fillW = Math.round(fuelBarW * Math.max(0, Math.min(1, ratio)));
  ctx.fillStyle = ratio > 0.5 ? COLOR.fuelHigh : ratio > 0.2 ? COLOR.fuelMid : COLOR.fuelLow;
  ctx.fillRect(fuelBarX, fuelBarY, fillW, fuelBarH);

  // CHALLENGING STAGE intro banner (sits over the play area, not the sidebar).
  const CHALLENGING_BANNER_TICKS = 120;
  if (
    state.phase === 'playing' &&
    state.challenging &&
    state.tick - state.stageStartTick < CHALLENGING_BANNER_TICKS
  ) {
    drawPlayBanner(ctx, 'CHALLENGING STAGE', COLOR.banner, 24);
  }

  // Phase banner (over the play area).
  if (state.phase !== 'playing') {
    if (state.phase === 'title') {
      drawPlayPanel(ctx, 190);
      if (ri.logo) {
        drawLogo(ctx, ri.logo, 75, LAYOUT.playH / 2 - 86, 330, 110);
      } else {
        drawCentered(ctx, 'RALLY-XY', COLOR.text, 'bold 44px ui-monospace, Menlo, monospace', -44);
      }
      drawCentered(ctx, 'PRESS ENTER TO START', COLOR.banner, 'bold 14px ui-monospace, Menlo, monospace', 40);
      drawCentered(
        ctx,
        'arrows · space=smoke · p=pause · m=music · f=fullscreen',
        COLOR.textDim,
        '10px ui-monospace, Menlo, monospace',
        66,
      );
    } else if (state.phase === 'gameover') {
      drawPlayPanel(ctx, 150);
      drawCentered(ctx, 'GAME OVER', COLOR.banner, 'bold 28px ui-monospace, Menlo, monospace', -28);
      drawCentered(ctx, `FINAL SCORE ${state.score}`, COLOR.text, 'bold 14px ui-monospace, Menlo, monospace', 0);
      drawCentered(ctx, 'PRESS ENTER TO START', COLOR.banner, 'bold 12px ui-monospace, Menlo, monospace', 28);
    } else {
      const label =
        state.phase === 'cleared'
          ? 'STAGE CLEAR'
          : state.phase === 'dying'
            ? state.deathCause === 'crash'
              ? 'CRASHED'
              : 'OUT OF FUEL'
            : 'PAUSED';
      drawPlayPanel(ctx, 80);
      drawCentered(ctx, label, COLOR.banner, 'bold 28px ui-monospace, Menlo, monospace', 0);
    }
  }
}

function drawLogo(
  ctx: CanvasRenderingContext2D,
  logo: CanvasImageSource,
  x: number,
  y: number,
  width: number,
  height: number,
): void {
  ctx.save();
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(logo, x, y, width, height);
  ctx.restore();
}

function drawRow(
  ctx: CanvasRenderingContext2D,
  colX: number,
  valX: number,
  y: number,
  label: string,
  value: string,
): void {
  ctx.textAlign = 'left';
  ctx.font = '10px ui-monospace, Menlo, monospace';
  ctx.fillStyle = '#475174';
  ctx.fillText(label, colX, y);
  ctx.textAlign = 'right';
  ctx.fillStyle = '#e6e6f0';
  ctx.font = 'bold 12px ui-monospace, Menlo, monospace';
  ctx.fillText(value, valX, y);
}

function drawPlayBanner(
  ctx: CanvasRenderingContext2D,
  label: string,
  color: string,
  fontPx: number,
): void {
  const pw = LAYOUT.playW;
  const ph = LAYOUT.playH;
  const bannerH = 60;
  const bannerY = Math.round(ph * 0.35) - bannerH / 2;
  ctx.fillStyle = 'rgba(6, 6, 12, 0.55)';
  ctx.fillRect(0, bannerY, pw, bannerH);
  ctx.fillStyle = color;
  ctx.font = `bold ${fontPx}px ui-monospace, Menlo, monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, pw / 2, bannerY + bannerH / 2);
}

function drawPlayPanel(ctx: CanvasRenderingContext2D, height: number): void {
  const pw = LAYOUT.playW;
  const ph = LAYOUT.playH;
  ctx.fillStyle = 'rgba(6, 6, 12, 0.85)';
  ctx.fillRect(0, ph / 2 - height / 2, pw, height);
}

function drawCentered(
  ctx: CanvasRenderingContext2D,
  text: string,
  color: string,
  font: string,
  dy: number,
): void {
  ctx.fillStyle = color;
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, LAYOUT.playW / 2, LAYOUT.playH / 2 + dy);
}

export function drawPauseBanner(ctx: CanvasRenderingContext2D): void {
  drawPlayPanel(ctx, 80);
  drawCentered(ctx, 'PAUSED', COLOR.banner, 'bold 28px ui-monospace, Menlo, monospace', 0);
}
